import { Helmet } from 'react-helmet-async';
import { SITE_URL, SITE_NAME, SITE_OG_IMAGE } from '../constants/site';

/**
 * Per-page SEO: unique title/description, a self-referencing canonical on the
 * real domain, Open Graph + Twitter tags, and optional JSON-LD structured data.
 *
 * Because this uses react-helmet-async, react-snap bakes these tags into the
 * prerendered static HTML at build time, so crawlers (and social/AI bots that
 * don't run JS) see the correct per-page metadata.
 *
 * @param {string} path   Route path for the canonical URL, e.g. '/' or '/about'
 * @param {object|object[]} jsonLd  One or more JSON-LD objects
 */
export default function Seo({
  title,
  description,
  path = '/',
  image = SITE_OG_IMAGE,
  jsonLd,
}) {
  const canonical = `${SITE_URL}${path}`;
  const absImage = image.startsWith('http') ? image : `${SITE_URL}${image}`;
  const blocks = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={absImage} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absImage} />

      {blocks.map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}
